import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { ServiceList } from "../components/ServiceList";
import { usePageMeta } from "../hooks/usePageMeta";
import { buildWhatsAppLink } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowRight } from "lucide-react";

const ServiceBlock = ({ data, image, reverse, eyebrow }) => (
  <div className={`grid md:grid-cols-12 gap-10 md:gap-20 items-center ${reverse ? "md:[direction:rtl]" : ""}`}>
    <Reveal className="md:col-span-6 [direction:ltr]" delay={0.1}>
      <img
        src={image}
        alt={data.title}
        loading="lazy"
        className="w-full h-[480px] md:h-[640px] object-cover aw-grayscale"
      />
    </Reveal>
    <div className="md:col-span-6 [direction:ltr]">
      <Reveal>
        <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
          — {eyebrow}
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-serif-aw text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-12 leading-[1.05]">
          {data.title}
        </h2>
      </Reveal>
      <div className="space-y-8">
        {data.items.map((item, idx) => (
          <Reveal key={item.name} delay={0.15 + idx * 0.08}>
            <div className="border-t border-black/15 pt-6 flex gap-6">
              <span className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/40 mt-1 w-8 shrink-0">
                0{idx + 1}
              </span>
              <div>
                <h3 className="font-serif-aw text-xl md:text-2xl font-normal mb-2">{item.name}</h3>
                <p className="font-sans-aw text-sm md:text-base text-black/65 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

const Services = () => {
  const { t, images } = useLanguage();
  const { hash } = useLocation();
  usePageMeta(t.meta.services);

  useEffect(() => {
    if (!hash) return;
    const id = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(id);
  }, [hash]);

  return (
    <div data-testid="services-page" className="bg-white">
      <section className="pt-40 md:pt-48 pb-2 md:pb-4 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
              — {t.services.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-aw text-5xl md:text-6xl lg:text-7xl font-light tracking-tight max-w-4xl leading-[1.05]">
              {t.services.heading}
            </h1>
          </Reveal>
        </div>
      </section>

      <ServiceList />

      <section data-testid="services-watches" className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto">
          <ServiceBlock data={t.services.watches} image={images.watch_service} reverse={false} eyebrow="Métier 01" />
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="border-t border-black/10" />
      </div>

      <section data-testid="services-goldsmith" className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto">
          <ServiceBlock data={t.services.goldsmith} image={images.goldsmith} reverse={true} eyebrow="Métier 02" />
        </div>
      </section>

      <section className="bg-black text-white px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif-aw text-3xl md:text-5xl font-light leading-tight">
              {t.closing.services_title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-sans-aw text-white/70 mt-8 text-base md:text-lg leading-relaxed">
              {t.closing.services_subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              data-testid="services-whatsapp-cta"
              href={buildWhatsAppLink(t.whatsapp.default_message)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 font-sans-aw text-[11px] tracking-luxury uppercase border border-white/40 hover:border-white px-8 py-4 transition-colors duration-500"
            >
              {t.contact.whatsapp_cta}
              <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
